import { fetchLiveStream, Meta } from 'common/apiService';
import Layout from 'common/Layout/Layout';
import WidgetManager from 'common/Components/Widgets/WidgetManager';
import { __t } from 'common/i18n';
import xIPCountry from '../../common/apiService/xIPCountry';
import * as Sentry from '@sentry/nextjs';
import ContentList from '../../common/ContentList';
import InfiniteScroll from '../../common/InfiniteScroll';
import ContentListLoading from '../../common/ContentListLoading';
import usePagination from '../../common/usePagination';
import React from 'react';
import styled from '@emotion/styled';
import { Flex, Text } from '../../common/styles';
import { IconLiveBlink, IconEmptyLiveSchedule } from '../../common/icons';
import Seo from '../../common/Seo';
import Breadcrumb from '../../common/Breadcrumb';
import CompactHeader, { canShowCompactHeader } from '../../common/Components/CompactHeader';

type LivePageProps = {
  data: any;
  initialUrl: string;
  meta: Meta;
};

export default function LivePage({ data, initialUrl, meta }: LivePageProps) {
  const { title, body, widgets, compact_header, show_header_details, videos: archiveVideos } = data;

  const initialData = {
    data: archiveVideos || [],
    meta,
  };
  const { data: contents, getInfiniteScrollProps, hasMore } = usePagination({ initialUrl, initialData });
  const seoTitle = `${title || __t('common.live')} | ${__t('static_siteName')}`;
  const seoDescription = body?.substring(0, 155) || 'تماشای پخش زنده در فیلمنت';
  const compact = compact_header && canShowCompactHeader(widgets);

  return (
    <>
      <Seo title={seoTitle} description={seoDescription} path="/live" />
      <Breadcrumb
        items={[
          { name: __t('common.home'), url: '/' },
          { name: __t('common.live'), url: '/live' },
        ]}
      />
      <Layout disableFooter={hasMore}>
        {!widgets?.length && !contents?.length && <EmptyScheduleState />}
        {/* Title/body are page-level header content, not part of any widget. For the compact header
            they sit on the first widget (teaser or slider); otherwise the title goes above the widgets. */}
        {show_header_details &&
          (compact ? (
            <CompactHeader title={title} body={body} icon={<IconLiveBlink />} />
          ) : (
            title && (
              <Text variant="h1" mb={2}>
                {title}
              </Text>
            )
          ))}
        <WidgetManager widgets={widgets} headerInfo={{ compact }} />
        {!!contents?.length && (
          <ArchiveWrapper>
            <Text variant="h2" weight="medium" mb={1}>
              {__t('live.all_archived_content')}
            </Text>
            <InfiniteScroll {...getInfiniteScrollProps()} loader={<ContentListLoading />}>
              <ContentList items={contents} />
            </InfiniteScroll>
          </ArchiveWrapper>
        )}
      </Layout>
    </>
  );
}

const ArchiveWrapper = styled.div(() => ({
  marginTop: '4.5em',
}));

export const EmptyScheduleState = () => {
  return <>
    <Flex direction='column' align="center" justify="center" mt={8}>
      <IconEmptyLiveSchedule />
      <Text mt={4}>{__t('live.no_conductors')}</Text></Flex>
  </>;
};

export async function getServerSideProps({ req, res }) {
  xIPCountry({ req, res });

  try {
    const { data, meta } = await fetchLiveStream();
    return {
      props: {
        data,
        meta,
        initialUrl: meta.videos_next_url || '',
      },
    };
  } catch (err) {
    Sentry.withScope((scope) => {
      scope.setTag('Status', err.response?.status);
      scope.setTag('StatusText', err.response?.statusText);
      scope.setTag('Url', err.response?.config?.url);
      Sentry.captureException(err);
    });

    return { notFound: true };
  }
}
